import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fetchSprintTasks } from '@/lib/notion-client';
import type { SprintTask, Sprint } from '@/lib/types';

interface SprintContextType {
  sprintTasks: SprintTask[];
  sprints: Sprint[];
  loadingSprints: boolean;
  errorSprints: string | null;
  refreshSprints: (force?: boolean) => Promise<void>;
}

const SprintContext = createContext<SprintContextType | undefined>(undefined);

export function SprintProvider({ children }: { children: React.ReactNode }) {
  const [sprintTasks, setSprintTasks] = useState<SprintTask[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loadingSprints, setLoadingSprints] = useState(false);
  const [errorSprints, setErrorSprints] = useState<string | null>(null);

  const refreshSprints = useCallback(async (force = false) => {
    try {
      setLoadingSprints(true);
      setErrorSprints(null);

      const wpConfig = (window as any).WP_COCKPIT;
      const isDev = wpConfig?.devMode || false;
      
      if (isDev) {
        console.log('[SprintContext] Starting fetch, force:', force);
      }

      const data = await fetchSprintTasks(force);
      
      if (isDev) {
        console.log('[SprintContext] Received data:', data);
      }
      
      if (data?.data?.tasks && Array.isArray(data.data.tasks)) {
        if (isDev && data.data.tasks.length > 0) {
          console.log('[SprintContext] First task raw data:', data.data.tasks[0]);
          console.log('[SprintContext] First task contributors:', data.data.tasks[0].contributors);
          console.log('[SprintContext] First task assignee:', data.data.tasks[0].assignee);
          console.log('[SprintContext] First task assignee type:', typeof data.data.tasks[0].assignee);
          console.log('[SprintContext] First task assignee is array:', Array.isArray(data.data.tasks[0].assignee));
        }
        
        const mappedTasks: SprintTask[] = data.data.tasks.map((task: any) => {
          // Filter out empty strings from sprints array
          const cleanedSprints = Array.isArray(task.sprints) 
            ? task.sprints.map(String).filter((s: string) => s.trim() !== '')
            : [];
          
          return {
            id: String(task.id),
            title: task.title ?? task.name ?? "",
            status: task.status ?? "Not Started",
            due: task.dueDate || task.due ? new Date(task.dueDate || task.due) : null,
            contributors: Array.isArray(task.contributors) ? task.contributors.map(String) : [],
            assignee: Array.isArray(task.assignee) ? task.assignee.map(String) : undefined,
            description: task.description ?? task.desc ?? "",
            estimatedHours: task.estimatedHours ? parseFloat(task.estimatedHours) : (task.estimated_hours ? parseFloat(task.estimated_hours) : undefined),
            realisedHours: task.realisedHours ? parseFloat(task.realisedHours) : (task.realised_hours ? parseFloat(task.realised_hours) : undefined),
            tags: Array.isArray(task.tags) ? task.tags.map(String) : [],
            url: task.url || task.notion_url || undefined,
            markdownContent: task.markdownContent || task.markdown_content || undefined,
            sprints: cleanedSprints,
            projectId: task.projectId || task.project_id || undefined,
            priority: task.priority || undefined,
          };
        });
        setSprintTasks(mappedTasks);
        
        if (isDev) {
          console.log('[SprintContext] Mapped sprint tasks:', mappedTasks.length);
          if (mappedTasks.length > 0) {
            console.log('[SprintContext] First mapped task:', mappedTasks[0]);
            console.log('[SprintContext] First mapped task contributors:', mappedTasks[0].contributors);
            console.log('[SprintContext] First mapped task assignee:', mappedTasks[0].assignee);
          }
          const tasksWithContributors = mappedTasks.filter(t => t.contributors && t.contributors.length > 0);
          console.log('[SprintContext] Tasks with contributors:', tasksWithContributors.length, '/', mappedTasks.length);
          const tasksWithAssignee = mappedTasks.filter(t => t.assignee && t.assignee.length > 0);
          console.log('[SprintContext] Tasks with assignee:', tasksWithAssignee.length, '/', mappedTasks.length);
          
          // Debug sprint assignment
          const tasksWithSprints = mappedTasks.filter(t => t.sprints && t.sprints.length > 0);
          const tasksWithoutSprints = mappedTasks.filter(t => !t.sprints || t.sprints.length === 0);
          console.log('[SprintContext] === SPRINT ASSIGNMENT DEBUG ===');
          console.log('[SprintContext] Tasks WITH sprints:', tasksWithSprints.length, '/', mappedTasks.length);
          console.log('[SprintContext] Tasks WITHOUT sprints (unassigned):', tasksWithoutSprints.length, '/', mappedTasks.length);
          if (tasksWithoutSprints.length > 0) {
            console.log('[SprintContext] Sample unassigned tasks:', tasksWithoutSprints.slice(0, 5).map(t => ({
              id: t.id,
              title: t.title,
              projectId: t.projectId,
              sprints: t.sprints,
              status: t.status
            })));
          }
          if (tasksWithSprints.length > 0) {
            console.log('[SprintContext] Sample tasks with sprints:', tasksWithSprints.slice(0, 3).map(t => ({
              id: t.id,
              title: t.title,
              sprints: t.sprints
            })));
          }
          
          // Debug raw data from API
          if (data.data.tasks.length > 0) {
            console.log('[SprintContext] === RAW API DATA DEBUG ===');
            console.log('[SprintContext] Raw tasks sample (first 3):', data.data.tasks.slice(0, 3).map((t: any) => ({
              id: t.id,
              title: t.title,
              sprints: t.sprints,
              sprintsType: typeof t.sprints,
              sprintsIsArray: Array.isArray(t.sprints),
              sprintsContainsEmptyString: Array.isArray(t.sprints) && t.sprints.some((s: any) => s === '' || (typeof s === 'string' && s.trim() === ''))
            })));
            
            // Count tasks with empty string sprints
            const tasksWithEmptyStringSprints = data.data.tasks.filter((t: any) => 
              Array.isArray(t.sprints) && t.sprints.length > 0 && t.sprints.every((s: any) => s === '' || (typeof s === 'string' && s.trim() === ''))
            );
            console.log('[SprintContext] Tasks with ONLY empty string sprints (should be unassigned):', tasksWithEmptyStringSprints.length);
          }
        }
      }

      if (data?.data?.sprints && Array.isArray(data.data.sprints)) {
        // Find current sprint first to calculate Next sprint dates
        const currentSprint = data.data.sprints.find((s: any) => s.status?.toLowerCase() === 'current');
        
        const mappedSprints: Sprint[] = data.data.sprints.map((sprint: any) => {
          let startDate = sprint.startDate || sprint.start_date || undefined;
          let endDate = sprint.endDate || sprint.end_date || undefined;
          
          // Calculate dates for "Next" sprints based on current sprint
          if (sprint.status?.toLowerCase() === 'next' && currentSprint) {
            const currentEnd = new Date(currentSprint.endDate || currentSprint.end_date);
            if (!isNaN(currentEnd.getTime())) {
              const nextStart = new Date(currentEnd);
              nextStart.setDate(currentEnd.getDate() + 1); // Day after current ends
              const nextEnd = new Date(nextStart);
              nextEnd.setDate(nextStart.getDate() + 6); // 7 day sprint
              
              startDate = nextStart.toISOString().split('T')[0];
              endDate = nextEnd.toISOString().split('T')[0];
            }
          }
          
          return {
            id: String(sprint.id),
            name: sprint.name ?? "",
            startDate,
            endDate,
            status: sprint.status || undefined,
            goal: sprint.goal || undefined,
          };
        });
        setSprints(mappedSprints);
        
        if (isDev) {
          console.log('[SprintContext] Mapped sprints:', mappedSprints.length, mappedSprints);
        }
      }
    } catch (err) {
      console.error('[SprintContext] Error fetching sprint data:', err);
      setErrorSprints(err instanceof Error ? err.message : 'Failed to load sprint data');
    } finally {
      setLoadingSprints(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refreshSprints();
  }, []);

  const value: SprintContextType = {
    sprintTasks,
    sprints,
    loadingSprints,
    errorSprints,
    refreshSprints,
  };

  return (
    <SprintContext.Provider value={value}>
      {children}
    </SprintContext.Provider>
  );
}

export function useSprints() {
  const context = useContext(SprintContext);
  if (context === undefined) {
    throw new Error('useSprints must be used within a SprintProvider');
  }
  return context;
}
